import React, { useRef } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import EnhancedSelect from '../common/EnhancedSelect';
import { handleKanaInputChange } from '../../utils/input';

// Defined outside to prevent re-declaration on every render
const InputContent = ({
  containerClasses,
  filtersRowClasses,
  searchRowClasses,
  inputClasses,
  searchButtonClasses,
  closeButtonClasses,
  searchQuery,
  handleInputChange,
  searchType,
  setSearchType,
  wordSearchBy,
  setWordSearchBy,
  isJapaneseInput,
  variant,
  onClose,
  inputRef,
}) => (
  <div className={containerClasses}>
    {/* Filters Row */}
    <div className={filtersRowClasses}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content-secondary whitespace-nowrap">Search in:</span>
          <EnhancedSelect
            id="search-type"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            options={[
              { value: 'words', label: 'Words' },
              { value: 'sentences', label: 'Sentences' },
            ]}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content-secondary whitespace-nowrap">Search by:</span>
          <EnhancedSelect
            id="search-by"
            value={wordSearchBy}
            onChange={(e) => setWordSearchBy(e.target.value)}
            options={[
              { value: 'kana', label: 'Kana' },
              { value: 'kanji', label: 'Kanji' },
            ]}
          />
        </div>
      </div>
    </div>

    {/* Search Input Row */}
    <div className={searchRowClasses}>
      <input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={handleInputChange}
        placeholder={`Search ${searchType}...`}
        className={inputClasses}
        lang={isJapaneseInput ? 'ja' : undefined}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck="false"
        autoFocus={variant === 'modal'}
      />
      <div className="flex items-center text-secondary">
        {variant === 'modal' && onClose && (
          <button type="button" onClick={onClose} className={closeButtonClasses} aria-label="Close search">
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
        {variant === 'page' && (
          <button type="submit" className={searchButtonClasses} aria-label="Search">
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  </div>
);

const SearchInput = ({
  searchQuery,
  setSearchQuery,
  searchType,
  setSearchType,
  wordSearchBy,
  setWordSearchBy,
  onSubmit,
  onClose,
  variant = 'page',
  wrapperClassName = '',
}) => {
  const inputRef = useRef(null);
  
  const handleInputChange = wordSearchBy === 'kana' 
    ? handleKanaInputChange(setSearchQuery, inputRef)
    : (e) => setSearchQuery(e.target.value);

  const isJapaneseInput = wordSearchBy === 'kana' || wordSearchBy === 'kanji';

  const containerClasses = clsx('flex flex-col w-full bg-background-secondary border border-border transition-all duration-200 shadow-sm', {
    'rounded-xl focus-within:shadow-md': variant === 'page',
    'rounded-t-xl border-b-0': variant === 'modal',
  });

  const filtersRowClasses = clsx('flex items-center gap-3 px-4 py-3 border-b border-border', {
    'bg-background-secondary/50': variant === 'page',
    'bg-gradient-to-r from-primary/10 to-secondary/10': variant === 'modal',
  });

  const searchRowClasses = clsx('flex items-center', {
    'rounded-b-xl bg-background-secondary': variant === 'page',
    'bg-background-primary/50 backdrop-blur-sm border-b border-border': variant === 'modal',
  });

  const inputClasses = clsx(
    'flex-grow min-w-0 px-4 ps-4 text-base bg-transparent focus:outline-none text-content-primary placeholder-content-secondary',
    {
      'sm:text-lg': variant === 'page',
    }
  );

  const searchButtonClasses = clsx(
    'flex-shrink-0 flex items-center justify-center px-5 py-4 bg-primary hover:bg-primary-hover text-white transition-colors focus:outline-none focus:bg-primary-hover',
    {
      'rounded-br-xl': variant === 'page',
    }
  );

  const closeButtonClasses = clsx(
    'flex-shrink-0 flex items-center justify-center px-4 py-4 text-content-secondary hover:text-content-primary transition-colors focus:outline-none rounded-md',
    {
      'hover:bg-secondary/20': variant === 'modal',
      'hover:bg-background-primary': variant !== 'modal',
    }
  );

  const inputContentProps = {
    containerClasses,
    filtersRowClasses,
    searchRowClasses,
    inputClasses,
    searchButtonClasses,
    closeButtonClasses,
    searchQuery,
    handleInputChange,
    searchType,
    setSearchType,
    wordSearchBy,
    setWordSearchBy,
    isJapaneseInput,
    variant,
    onClose,
    inputRef,
  };

  return (
    <div className={wrapperClassName}>
      {variant === 'page' ? (
        <form onSubmit={onSubmit} className="w-full">
          <InputContent {...inputContentProps} />
        </form>
      ) : (
        <InputContent {...inputContentProps} />
      )}
    </div>
  );
};

export default SearchInput;